import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

/* Адаптированный хук из либы react-use:
 * https://github.com/streamich/react-use/blob/master/src/useLocalStorage
 * https://usehooks-ts.com/react-hook/use-local-storage */
export const useLocalStorage = <T>(
  key: string,
  initialValue?: T
): [T | undefined, Dispatch<SetStateAction<T | undefined>>, () => void] => {
  // Синхронизация между разными вкладами.
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === key) {
        const newValue = event.newValue
          ? JSON.parse(event.newValue)
          : undefined;
        setState(newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [key]);

  const initializer = useRef((key: string) => {
    try {
      const localStorageValue = localStorage.getItem(key);

      if (localStorageValue !== null) {
        return JSON.parse(localStorageValue);
      }

      if (initialValue) {
        localStorage.setItem(key, JSON.stringify(initialValue));

        return initialValue;
      }
    } catch {
      return initialValue;
    }
  });

  const [state, setState] = useState<T | undefined>(() =>
    initializer.current(key)
  );

  useLayoutEffect(() => setState(initializer.current(key)), [key]);

  const set: Dispatch<SetStateAction<T | undefined>> = useCallback(
    (valOrFunc) => {
      try {
        const newState =
          typeof valOrFunc === 'function'
            ? // eslint-disable-next-line
              (valOrFunc as Function)(state)
            : valOrFunc;

        if (typeof newState === 'undefined') {
          return;
        }

        const parsedValue = JSON.stringify(newState);

        localStorage.setItem(key, parsedValue);
        setState(JSON.parse(parsedValue));
      } catch {
        console.error('useLocalStorage set action error');
      }
    },
    [key, state, setState]
  );

  const remove = useCallback(() => {
    try {
      localStorage.removeItem(key);
      setState(undefined);
    } catch {
      console.error('useLocalStorage remove action catch.');
    }
  }, [key, setState]);

  return [state, set, remove];
};
